/**
 * Notification Service
 * Handles all notification-related API calls and WebSocket connections
 */

import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

class NotificationService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Set();
    this.userId = null;
    this.isConnecting = false;
  }

  /**
   * Initialize WebSocket connection for real-time notifications
   * @param {string} userId - User identifier
   */
  connectWebSocket(userId) {
    if (!userId) {
      console.warn('Cannot connect WebSocket: userId is required');
      return;
    }

    this.userId = userId;

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    if (this.isConnecting) {
      console.log('WebSocket connection already in progress');
      return;
    }

    this.isConnecting = true;

    try {
      // Convert HTTP URL to WebSocket URL
      const wsUrl = config.bffUrl
        .replace('http://', 'ws://')
        .replace('https://', 'wss://');

      const url = `${wsUrl}/ws/notifications?userId=${encodeURIComponent(userId)}`;
      
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('WebSocket connected successfully');
        this.reconnectAttempts = 0;
        this.isConnecting = false;
        this.notifyListeners({ type: 'connection', status: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'notification') {
            this.notifyListeners(message.data);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.isConnecting = false;
        this.ws = null;

        // Attempt to reconnect
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Reconnecting in ${this.reconnectDelay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          
          setTimeout(() => {
            this.connectWebSocket(this.userId);
          }, this.reconnectDelay);
        } else {
          console.error('Max reconnection attempts reached');
          this.notifyListeners({ type: 'connection', status: 'failed' });
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.isConnecting = false;
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnectWebSocket() {
    if (this.ws) {
      // Only close if WebSocket is in OPEN or CONNECTING state
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000, 'Client disconnecting');
      }
      this.ws = null;
    }
    this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnection
  }

  /**
   * Subscribe to notification events
   * @param {Function} callback - Callback function to handle notifications
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners of a new notification
   * @param {Object} notification - Notification data
   */
  notifyListeners(notification) {
    this.listeners.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Get user's notifications with pagination
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Notifications and pagination info
   */
  async getNotifications(options = {}) {
    const {
      page = 1,
      limit = 50,
      unreadOnly = false,
      type = null,
      priority = null,
      userId = null,
    } = options;

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      unreadOnly: unreadOnly.toString(),
    });

    if (type) params.append('type', type);
    if (priority) params.append('priority', priority);
    if (userId) params.append('userId', userId);

    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/notifications?${params}`, {
      method: 'GET',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        ...(userId && { 'x-user-id': userId }),
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch notifications: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Create a new notification
   * @param {Object} notification - Notification data
   * @returns {Promise<Object>} Created notification
   */
  async createNotification(notification) {
    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/notifications`, {
      method: 'POST',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
      }),
      credentials: 'include',
      body: JSON.stringify(notification),
    });

    if (!response.ok) {
      throw new Error(`Failed to create notification: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response
   */
  async markAsRead(notificationId, userId) {
    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/notifications/${notificationId}/read`, {
      method: 'PATCH',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        'x-user-id': userId,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to mark notification as read: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Mark notification as acknowledged
   * @param {string} notificationId - Notification ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response
   */
  async markAsAcknowledged(notificationId, userId) {
    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/notifications/${notificationId}/acknowledge`, {
      method: 'PATCH',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        'x-user-id': userId,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to acknowledge notification: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Mark multiple notifications as read
   * @param {string[]} notificationIds - Array of notification IDs
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response
   */
  async markMultipleAsRead(notificationIds, userId) {
    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/notifications/read-multiple`, {
      method: 'PATCH',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        'x-user-id': userId,
      }),
      credentials: 'include',
      body: JSON.stringify({ notificationIds }),
    });

    if (!response.ok) {
      throw new Error(`Failed to mark notifications as read: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Mark all notifications as read
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response
   */
  async markAllAsRead(userId) {
    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/notifications/read-all`, {
      method: 'PATCH',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        'x-user-id': userId,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to mark all notifications as read: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Delete a notification
   * @param {string} notificationId - Notification ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Response
   */
  async deleteNotification(notificationId, userId) {
    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/notifications/${notificationId}?userId=${userId}`, {
      method: 'DELETE',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        'x-user-id': userId,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete notification: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Get unread notification count
   * @param {string} userId - User ID
   * @returns {Promise<number>} Unread count
   */
  async getUnreadCount(userId) {
    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/notifications/unread-count?userId=${userId}`, {
      method: 'GET',
      headers: getBffHeaders({
        'Content-Type': 'application/json',
        'x-user-id': userId,
      }),
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to get unread count: ${response.statusText}`);
    }

    const data = await response.json();
    return data.count;
  }

  /**
   * Send a ping to keep connection alive
   */
  ping() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    }
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
